/**
 * Fleet Optimizer for Fixed Routes
 * 
 * Main class for calculating vehicle requirements and generating fleet configurations
 * for fixed route services. Designed to support Phase 1B (shift allocation) and Phase 2 (mixed fleet).
 */

import type { FixedRoute } from '../../types/fixedRoutes';
import type { RideSchema } from '../../types/simulation';
import type { BreakPeriod } from '../../types/simulation';
import type { Line } from '../../context/context';
import type {
  FixedRouteFleetConfig,
  FleetResult,
  ValidationResult,
  MixedFleetConfig,
} from './fleetTypes';
import {
  calculateRouteLoopDuration,
  calculateMinimumVehicles,
} from './vehicleCalculator';
import {
  calculateDedicatedMode,
  calculateSharedMode,
} from './multiRouteOptimizer';
import { generateVehicleAssignments } from './routeAssignmentGenerator';
import { FixedRouteShiftAllocator } from './fixedRouteShiftAllocator';
import type { VehicleAssignment } from './fleetTypes';
import type { GeneratorConfig } from '../../components/map/components/auto-fleet-generator/types';
import { ShiftGenerator } from '../../components/map/components/auto-fleet-generator/shiftGenerator';
import {
  buildHourlyDemandDistribution,
  calculateVehicleRequirements,
  calculateHourlyVehicleRequirements,
  extractServiceHours,
} from '../../components/map/components/auto-fleet-generator/tripAnalysis';
import { parseTime, formatTime } from '../../components/map/components/auto-fleet-generator/timeUtils';
import {
  calculateFixedFirst,
  calculateOnDemandFirst,
  calculateEqualPriority,
  calculateWeighted,
} from './mixedFleetOptimizer';

/**
 * FleetOptimizer class for unified fleet optimization
 * 
 * Supports:
 * - Fixed route optimization (Phase 1A - this PRD)
 * - On-demand optimization (existing)
 * - Mixed optimization (Phase 2 - placeholder)
 */
export class FleetOptimizer {
  /**
   * Calculate fixed route fleet requirements and generate vehicle assignments
   * 
   * @param routes - Array of fixed routes to optimize
   * @param config - Fleet configuration including vehicle specs and sharing mode
   * @returns Fleet result with vehicle count, assignments, and summary
   */
  calculateFixedRouteFleet(
    routes: FixedRoute[],
    config: FixedRouteFleetConfig
  ): FleetResult {
    // 1. Validate routes
    const validationResult = this.validateRoutes(routes);
    if (!validationResult.isValid) {
      throw new Error(
        `Route validation failed: ${validationResult.errors.join(', ')}`
      );
    }

    // 2. Determine sharing mode
    const sharingMode = config.vehicleSharingMode;

    // 3. Calculate vehicle requirements
    const trafficMultiplier = config.trafficMultiplier || 1.0;
    let vehicleCount: number;

    if (sharingMode === 'dedicated') {
      vehicleCount = calculateDedicatedMode(routes, trafficMultiplier);
    } else {
      vehicleCount = calculateSharedMode(routes, trafficMultiplier);
    }

    // 4. Generate vehicle assignments
    const vehicleAssignments = generateVehicleAssignments(
      routes,
      vehicleCount,
      sharingMode,
      trafficMultiplier
    );

    // 5. Calculate operating hours coverage
    const operatingHoursCoverage = this.calculateOperatingHoursCoverage(routes);

    // 6. Build summary
    const summary = {
      totalVehicles: vehicleCount,
      routesServed: routes.length,
      operatingHoursCoverage,
      sharingMode,
    };

    return {
      vehicleCount,
      vehicleAssignments,
      summary,
    };
  }

  /**
   * Allocate shifts to fixed route fleet assignments
   * 
   * This method organizes vehicle assignments into shifts based on shift constraints
   * and schedules breaks according to configuration rules.
   * 
   * @param assignments - Initial vehicle assignments from calculateFixedRouteFleet
   * @param shiftConfig - Generator configuration with shift allocation settings
   * @returns Updated vehicle assignments with shifts organized and breaks scheduled
   */
  allocateShiftsToFixedRouteFleet(
    assignments: VehicleAssignment[],
    shiftConfig: GeneratorConfig
  ): VehicleAssignment[] {
    try {
      // Create shift allocator instance
      const allocator = new FixedRouteShiftAllocator(assignments, shiftConfig);
      
      // Allocate shifts to all assignments
      const updatedAssignments = allocator.allocateShifts(assignments);
      
      return updatedAssignments;
    } catch (error) {
      const errorMessage = error instanceof Error 
        ? error.message 
        : 'Unknown error during shift allocation';
      throw new Error(`Shift allocation failed: ${errorMessage}`);
    }
  }

  /**
   * Calculate on-demand fleet requirements and generate vehicle assignments
   * 
   * @param trips - Array of on-demand trips (RideSchema[] or Line[])
   * @param config - Fleet configuration compatible with GeneratorConfig
   * @returns Fleet result for on-demand services
   */
  calculateOnDemandFleet(
    trips: RideSchema[] | Line[],
    config: GeneratorConfig
  ): FleetResult {
    // 1. Validate trips
    if (!trips || trips.length === 0) {
      throw new Error('At least one trip must be provided for on-demand fleet calculation');
    }

    // 2. Convert trips to Line format if needed (RideSchema needs conversion)
    const tripLines = this.convertTripsToLines(trips);

    // 3. Calculate vehicle requirements based on trip demand
    const tripsPerVehiclePerHour = config.tripsPerVehiclePerHour || 5;
    const hourlyDemand = buildHourlyDemandDistribution(tripLines);
    const vehicleCount = calculateVehicleRequirements(hourlyDemand, tripsPerVehiclePerHour);
    const hourlyVehicleRequirements = calculateHourlyVehicleRequirements(hourlyDemand, tripsPerVehiclePerHour);

    // 4. Extract service hours from trips
    const serviceHours = extractServiceHours(tripLines);
    
    // 5. Update config with calculated values
    const onDemandConfig: GeneratorConfig = {
      ...config,
      mode: 'trip-layer', // Use trip-layer mode for ShiftGenerator compatibility
      manualVehicleCount: vehicleCount,
      serviceStartTime: config.serviceStartTime || serviceHours.start,
      serviceEndTime: config.serviceEndTime || serviceHours.end,
      hourlyVehicleRequirements,
      demandCurve: hourlyDemand,
    };

    // 6. Generate shifts using ShiftGenerator
    const shiftGenerator = new ShiftGenerator(onDemandConfig);
    const generatedShifts = shiftGenerator.generateShifts();

    // 7. Convert GeneratedShift to VehicleAssignment format
    const vehicleAssignments: VehicleAssignment[] = generatedShifts.map((shift, index) => {
      // Find the earliest start and latest end time across all operating periods
      let earliestStart: string | null = null;
      let latestEnd: string | null = null;

      shift.operatingPeriods.forEach((period) => {
        if (!earliestStart || compareTimes(period.start, earliestStart) < 0) {
          earliestStart = period.start;
        }
        if (!latestEnd || compareTimes(period.end, latestEnd) > 0) {
          latestEnd = period.end;
        }
      });

      // For on-demand, we create one VehicleAssignment per shift with empty routeAssignments
      // Store the operating periods with serviceType for later conversion in fleetConverter
      return {
        vehicleId: shift.vehicleId,
        vehicleName: shift.vehicleName,
        routeAssignments: [], // Empty for on-demand - no fixed routes
        operatingPeriod: {
          startTime: earliestStart || serviceHours.start,
          endTime: latestEnd || serviceHours.end,
        },
        // Store on-demand periods similar to how shiftBreaks are stored
        onDemandPeriods: shift.operatingPeriods.map((period) => ({
          start: period.start,
          end: period.end,
          breaks: period.breaks || [],
          serviceType: 'on-demand' as const,
        })),
      } as VehicleAssignment & { 
        onDemandPeriods?: Array<{ 
          start: string; 
          end: string; 
          breaks: BreakPeriod[]; 
          serviceType: 'on-demand' 
        }> 
      };
    });

    // 8. Calculate operating hours coverage
    const operatingHoursCoverage = `${serviceHours.start} - ${serviceHours.end}`;

    // 9. Build summary
    const summary = {
      totalVehicles: vehicleCount,
      routesServed: 0, // No routes for on-demand
      operatingHoursCoverage,
      sharingMode: 'dedicated' as const, // On-demand uses dedicated mode conceptually
    };

    return {
      vehicleCount,
      vehicleAssignments,
      summary,
    };
  }

  /**
   * Calculate on-demand vehicle requirements based on trips per vehicle per hour
   * 
   * @param trips - Array of on-demand trips (RideSchema[] or Line[])
   * @param tripsPerVehiclePerHour - Expected trips a vehicle can handle per hour (default: 5)
   * @returns Number of vehicles needed based on peak demand
   */
  calculateOnDemandVehicleRequirements(
    trips: RideSchema[] | Line[],
    tripsPerVehiclePerHour: number = 5
  ): number {
    const tripLines = this.convertTripsToLines(trips);
    const hourlyDemand = buildHourlyDemandDistribution(tripLines);
    return calculateVehicleRequirements(hourlyDemand, tripsPerVehiclePerHour);
  }

  /**
   * Build hourly demand timeline from trip data
   * 
   * @param trips - Array of on-demand trips (RideSchema[] or Line[])
   * @returns Object mapping hour (0-23) to trip count
   */
  buildHourlyDemandTimeline(trips: RideSchema[] | Line[]): Record<number, number> {
    const tripLines = this.convertTripsToLines(trips);
    return buildHourlyDemandDistribution(tripLines);
  }

  /**
   * Identify on-demand service periods from trip data
   * 
   * @param trips - Array of on-demand trips (RideSchema[] or Line[])
   * @returns Object with start and end time in HH:MM format
   */
  identifyOnDemandServicePeriods(trips: RideSchema[] | Line[]): { start: string; end: string } {
    const tripLines = this.convertTripsToLines(trips);
    return extractServiceHours(tripLines);
  }

  /**
   * Calculate hourly vehicle requirements for on-demand service
   * 
   * @param trips - Array of on-demand trips (RideSchema[] or Line[])
   * @param tripsPerVehiclePerHour - Expected trips per vehicle per hour (default: 5)
   * @returns Object mapping hour (0-23) to required vehicle count
   */
  calculateHourlyOnDemandVehicleRequirements(
    trips: RideSchema[] | Line[],
    tripsPerVehiclePerHour: number = 5
  ): Record<number, number> {
    const tripLines = this.convertTripsToLines(trips);
    const hourlyDemand = buildHourlyDemandDistribution(tripLines);
    return calculateHourlyVehicleRequirements(hourlyDemand, tripsPerVehiclePerHour);
  }

  /**
   * Convert trips to Line format for compatibility with trip analysis functions
   * 
   * @param trips - Array of trips (RideSchema[] or Line[])
   * @returns Array of Line objects
   */
  private convertTripsToLines(trips: RideSchema[] | Line[]): Line[] {
    // Check if already Line format by checking for Line-specific properties
    if (trips.length > 0 && 'type' in trips[0] && trips[0].type === 'line') {
      return trips as Line[];
    }

    // Convert RideSchema to Line format
    return (trips as RideSchema[]).map((ride, index) => {
      // Extract request time
      const requestTime = ride.request_time || ride.created_at;
      
      return {
        id: ride.ride_id || ride.external_reference_id || `trip-${index}`,
        type: 'line' as const,
        ride_id: ride.ride_id || ride.external_reference_id,
        from_latitude: ride.from_latitude,
        from_longitude: ride.from_longitude,
        to_latitude: ride.to_latitude,
        to_longitude: ride.to_longitude,
        from_address: ride.from_address,
        to_address: ride.to_address,
        request_time: requestTime,
        request_type: ride.request_type,
        // Store as any to match Line interface flexibility
      } as any as Line;
    });
  }

  /**
   * Calculate mixed fleet requirements and generate vehicle assignments
   * Supports both fixed routes and on-demand trips with priority-based optimization
   * 
   * @param routes - Array of fixed routes
   * @param trips - Array of on-demand trips (RideSchema[] or Line[])
   * @param config - Mixed fleet configuration with priority mode and settings
   * @returns Fleet result for mixed services
   */
  calculateMixedFleet(
    routes: FixedRoute[],
    trips: RideSchema[] | Line[],
    config: MixedFleetConfig
  ): FleetResult {
    // 1. Validate inputs
    if (!routes || routes.length === 0) {
      throw new Error('At least one fixed route must be provided for mixed fleet calculation');
    }
    
    if (!trips || trips.length === 0) {
      throw new Error('At least one trip must be provided for mixed fleet calculation');
    }
    
    // Validate routes
    const routeValidation = this.validateRoutes(routes);
    if (!routeValidation.isValid) {
      throw new Error(
        `Route validation failed: ${routeValidation.errors.join(', ')}`
      );
    }
    
    // Validate priority mode configuration
    if (config.priorityMode === 'weighted') {
      if (config.fixedRouteWeight === undefined || config.onDemandWeight === undefined) {
        throw new Error('Weighted priority mode requires both fixedRouteWeight and onDemandWeight to be specified');
      }
      if (config.fixedRouteWeight < 0 || config.fixedRouteWeight > 100) {
        throw new Error('fixedRouteWeight must be between 0 and 100');
      }
      if (config.onDemandWeight < 0 || config.onDemandWeight > 100) {
        throw new Error('onDemandWeight must be between 0 and 100');
      }
    }
    
    // 2. Switch based on priority mode to call appropriate algorithm
    let result: FleetResult;
    
    switch (config.priorityMode) {
      case 'fixed-first':
        result = calculateFixedFirst(routes, trips, config);
        break;
        
      case 'on-demand-first':
        result = calculateOnDemandFirst(routes, trips, config);
        break;
        
      case 'equal':
        result = calculateEqualPriority(routes, trips, config);
        break;
        
      case 'weighted':
        result = calculateWeighted(routes, trips, config);
        break;
        
      default:
        throw new Error(`Unknown priority mode: ${(config as any).priorityMode}`);
    }
    
    // 3. Result already contains vehicle assignments and summary
    // The mixed fleet algorithms handle combining assignments into unified fleet format
    return result;
  }

  /**
   * Calculate route loop duration (helper method)
   * 
   * @param route - The fixed route
   * @param trafficMultiplier - Traffic multiplier (default: 1.0)
   * @returns Loop duration in minutes
   */
  calculateRouteLoopDuration(
    route: FixedRoute,
    trafficMultiplier: number = 1.0
  ): number {
    return calculateRouteLoopDuration(route, trafficMultiplier);
  }

  /**
   * Calculate minimum vehicles for a route (helper method)
   * 
   * @param route - The fixed route
   * @param trafficMultiplier - Traffic multiplier (default: 1.0)
   * @returns Minimum number of vehicles needed
   */
  calculateMinimumVehicles(
    route: FixedRoute,
    trafficMultiplier: number = 1.0
  ): number {
    return calculateMinimumVehicles(route, trafficMultiplier);
  }

  /**
   * Validate routes before fleet calculation
   * 
   * @param routes - Array of fixed routes to validate
   * @returns Validation result with errors and warnings
   */
  validateRoutes(routes: FixedRoute[]): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (routes.length === 0) {
      errors.push('At least one route must be selected');
      return { isValid: false, errors, warnings };
    }

    routes.forEach((route, index) => {
      const routePrefix = `Route "${route.name || route.id}" (${index + 1})`;

      // Check minimum stops
      if (!route.stops || route.stops.length < 2) {
        errors.push(`${routePrefix}: Must have at least 2 stops`);
      }

      // Validate start time
      const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
      if (!route.startTime || !timeRegex.test(route.startTime)) {
        errors.push(`${routePrefix}: Invalid or missing start time (must be HH:mm format)`);
      }

      // Validate end time (if specified)
      if (route.endTime && !timeRegex.test(route.endTime)) {
        errors.push(`${routePrefix}: Invalid end time format (must be HH:mm format)`);
      }

      // Circulator routes require end time
      if (route.isCirculator && !route.endTime) {
        errors.push(`${routePrefix}: Circulator routes require an end time`);
      }

      // Validate segments
      if (!route.segments || route.segments.length === 0) {
        errors.push(`${routePrefix}: Must have at least one segment with travel times`);
      } else {
        // Check that segments have travel times
        const segmentsWithoutTimes = route.segments.filter(
          (seg) => !seg.travelTimeMinutes || seg.travelTimeMinutes <= 0
        );
        if (segmentsWithoutTimes.length > 0) {
          errors.push(`${routePrefix}: Some segments are missing travel times`);
        }
      }

      // For circulators: validate loop duration can be calculated
      if (route.isCirculator) {
        try {
          const loopDuration = calculateRouteLoopDuration(route, 1.0);
          if (loopDuration <= 0) {
            warnings.push(`${routePrefix}: Calculated loop duration is ${loopDuration} minutes (may indicate missing data)`);
          }
        } catch (error) {
          errors.push(`${routePrefix}: Cannot calculate loop duration - ${error instanceof Error ? error.message : 'unknown error'}`);
        }
      }

      // Edge cases: very short or very long loop durations
      if (route.isCirculator && route.stops && route.stops.length >= 2 && route.segments && route.segments.length > 0) {
        try {
          const loopDuration = calculateRouteLoopDuration(route, 1.0);
          if (loopDuration < 5) {
            warnings.push(`${routePrefix}: Very short loop duration (${loopDuration} minutes) - may indicate incorrect configuration`);
          }
          if (loopDuration > 240) {
            warnings.push(`${routePrefix}: Very long loop duration (${loopDuration} minutes = ${Math.round(loopDuration / 60)} hours)`);
          }
        } catch (error) {
          // Already handled above
        }
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }

  /**
   * Calculate operating hours coverage from routes
   * 
   * @param routes - Array of fixed routes
   * @returns String representation of operating hours (e.g., "08:00 - 22:00")
   */
  private calculateOperatingHoursCoverage(routes: FixedRoute[]): string {
    if (routes.length === 0) {
      return 'No routes';
    }

    // Find earliest start time and latest end time
    let earliestStart: string | null = null;
    let latestEnd: string | null = null;

    routes.forEach((route) => {
      if (!earliestStart || compareTimes(route.startTime, earliestStart) < 0) {
        earliestStart = route.startTime;
      }

      const routeEnd = route.endTime || '22:00';
      if (!latestEnd || compareTimes(routeEnd, latestEnd) > 0) {
        latestEnd = routeEnd;
      }
    });

    return `${earliestStart || 'Unknown'} - ${latestEnd || 'Unknown'}`;
  }
}

/**
 * Helper function to compare two time strings (HH:mm format)
 */
function compareTimes(time1: string, time2: string): number {
  const [h1, m1] = time1.split(':').map(Number);
  const [h2, m2] = time2.split(':').map(Number);
  const minutes1 = h1 * 60 + m1;
  const minutes2 = h2 * 60 + m2;

  if (minutes1 < minutes2) return -1;
  if (minutes1 > minutes2) return 1;
  return 0;
}

